import { Button } from "@/components/ui/button";
import MediaHomeCard from "@/modules/home/components/media-list/MediaHomeCard";
import { useBookmarkStore } from "@/stores/bookmarkStore";
import { useCampaignStore } from "@/stores/campaignStore";
import { useMapUIStore } from "@/stores/mapUIStore";
import { useMapStore } from "@/stores/mapStore";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useEffect, useMemo, useRef } from "react";
import { useTypedTranslations } from "@/i18n/typed";
import { Empty } from "@/components/custom/empty";
import { Typography } from "@/components/custom/typography";
import { CustomIcon } from "@/components/custom/icons/custom-icon";
import { useMediaProductsInBounds } from "@/hooks/map-hooks/useMediaProductsInBounds";
import { useMediaFilterParams } from "@/hooks/map-hooks/useMediaFilterParams";
import { MediaProduct } from "@/services/graphql/generated";
import { useSearchConditionStore } from "@/stores/searchConditionStore";
import { useUser } from "@/stores/user";
import { USER_TYPE } from "@/lib/constants";

interface MediaListViewProps {
  onRequireLogin?: () => void;
  search?: string;
}

const MediaListView = ({ onRequireLogin, search = "" }: MediaListViewProps) => {
  const t = useTypedTranslations("mediaOnMap");
  const filterParams = useMediaFilterParams();
  const { isMyMedia } = useSearchConditionStore();
  const { userInfo } = useUser();
  const isAdvertiser = userInfo?.user?.user_type === USER_TYPE.advertiser;

  // const { markers } = useMarkerStore();
  const bounds = useMapStore((s) => s.bounds);
  const { products, refetch } = useMediaProductsInBounds({
    bounds,
    isMyProduct: userInfo?.user?.user_type === USER_TYPE.media ? isMyMedia : false,
    ...filterParams,
  });
  const markers = useMemo(() => products as unknown as MediaProduct[], [products]);
  const activePoiList = useMapUIStore((s) => s.activePoiList);
  const checkedPins = useMapUIStore((s) => s.checkedPins);
  const selectedPin = useMapUIStore((s) => s.selectedPin);
  const clearCheckedPin = useMapUIStore((s) => s.clearCheckedPin);
  const { setOpenCampaignDialog } = useCampaignStore();
  const filterBookmarkedOnly = useBookmarkStore((s) => s.filterBookmarkedOnly);

  const activeIds = new Set(activePoiList?.map((m) => m.id) ?? []);

  const keyword = search.trim().toLowerCase();

  const parentRef = useRef<HTMLDivElement>(null);
  const filtered = !keyword
    ? markers
    : markers.filter((m) => {
      const name = m.name ?? "";
      const normalizedName = name.normalize("NFKD").toLowerCase();
      const normalizedInput = keyword.normalize("NFKD");
      return normalizedName.includes(normalizedInput);
    });

  const filteredBookmark = filterBookmarkedOnly
    ? filtered.filter((m) =>
      useBookmarkStore.getState().getById(String(m.id), m.isMyBookmark ?? false)
    )
    : filtered;

  const rowVirtualizer = useVirtualizer({
    count: filteredBookmark.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 160,
  });

  const handleCancelAll = () => {
    clearCheckedPin();
  };

  const selectedIndex = selectedPin
    ? filteredBookmark.findIndex((m) => {
      const info = m.mediaGroups?.mediaGroups?.[0]?.medias?.medias?.[0]?.mediaInfo;
      return info ? `${info.lat},${info.lng}` === selectedPin : false;
    })
    : -1;

  useEffect(() => {
    if (selectedIndex >= 0) {
      rowVirtualizer.scrollToIndex(selectedIndex, {
        align: "center",
        behavior: "smooth",
      });
    }
  }, [selectedIndex, rowVirtualizer]);
  
  useEffect(() => {
    refetch();
  }, [isMyMedia])

  if (!filteredBookmark.length) return <Empty description={<div className="flex flex-col justify-center items-center gap-1 min-w-60">
    <CustomIcon name="SearchOff" className="w-12 h-12" />
    <Typography className="wrap-normal whitespace-pre-line">{t("empty.description")}</Typography>
  </div>} />

  return (
    <div
      ref={parentRef}
      className="overflow-y-auto h-full bg-gray-25 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-transparent hover:scrollbar-thumb-gray-25"
    >
      <div
        style={{
          minHeight: `${rowVirtualizer.getTotalSize()}px`,
          width: "100%",
          position: "relative",
        }}
      >
        {rowVirtualizer.getVirtualItems().map((virtual) => {
          const item = filteredBookmark[virtual.index];
          return (
            <div
              key={item.id}
              ref={rowVirtualizer.measureElement}
              data-index={virtual.index}
              className="absolute left-0 right-0"
              style={{
                top: 0,
                transform: `translateY(${virtual.start}px)`,
              }}
            >
              <MediaHomeCard
                data={item}
                activeIds={activeIds}
                onRequireLogin={() => onRequireLogin?.()}
                onBookmarkSuccess={refetch}
              />
            </div>
          );
        })}
      </div>

      {checkedPins.size > 0 && !isAdvertiser && (
        <div className="sticky bottom-0 left-0 right-0 bg-white z-10 flex justify-between items-center gap-2 p-2 shadow-xl/30">
          <Button
            onClick={handleCancelAll}
            variant="secondary"
            className="w-[95px] active:bg-gray-400 active:border-gray-500"
          >
            {t("actions.clearAll")}
          </Button>
          <Button
            onClick={() => {
              setOpenCampaignDialog(true, Array.from(checkedPins));
            }}
            className="px-4 py-3 leading-[120%] tracking-[-0.064px] w-[272px] text-center active:bg-primary-700"
          >
            {t("actions.addToCampaign")}({checkedPins.size})
          </Button>
        </div>
      )}
    </div>
  );
};

export default MediaListView;
